import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { appendBreadCrumb, setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "../../interfaces";
import { SentinelBasePage } from "../sentinel-base";
import {
  Box,
  Button,
  Header,
  Icon,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@cloudscape-design/components";
import { getPackageDependencyList } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { PackageDependency, PackageDependencyListResponse } from "src/core/sentinel/types";

import { useParams } from "react-router-dom";
export interface SentinelTeamDetailPageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const PackageDetailPage: React.FC<SentinelTeamDetailPageProps> = (
  // eslint-disable-next-line
  props: SentinelTeamDetailPageProps
) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [dependencyList, setDependencyList] = useState<PackageDependency[]>([]);
  const pageSize = 50;
  const { packageId } = useParams<{ packageId: string }>();
  useEffect(() => {
    const fetchDependencyList = async () => {
      if (!packageId) return;
      try {
        const response: any = await dispatch(
          getPackageDependencyList({
            sentinelController: props.sentinelController,
            packageDependencyListInput: { packageName: packageId },
          })
        );
        const list = response?.payload?.payload?.packageDependencyList;
        setDependencyList(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error(`Error for getting package dependencies: ${error}`, error);
      }
      setLoading(false);
    };
    fetchDependencyList();
  }, [packageId]);


  const filteredPackages = dependencyList.filter((d) => {
    const content = [
      d.version,
      d.packageName,
      d.dependencyName
     
    ]
      .join(" ")
      .toLowerCase();

    return (
      !filteringText || content.includes(filteringText.toLowerCase())
    );
  });

  const paginatedItems = filteredPackages.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const totalPages = Math.ceil(filteredPackages.length / pageSize);

  useEffect(() => {
    dispatch(setPageTitle("Package Detail"));
    dispatch(removeInfoMessage({ id: 'message_1' }));
  }, []);

  return (
    <SentinelBasePage>
      <Table
        items={paginatedItems}
        columnDefinitions={[
          {
            id: "packageName",
            header: "Package Name",
            cell: (e: PackageDependency) => e.packageName.split(':')[0],
            sortingField: "packageName",
            isRowHeader: true,
          },
          {
            id: "dependencyName",
            header: "Dependency Name",
            cell: (e: PackageDependency) => e.dependencyName,
            sortingField: "dependencyName",
            isRowHeader: true,
          },
          {
            id: "version",
            header: "Version",
            cell: (e: PackageDependency) => e.version,
            sortingField: "version",
            isRowHeader: true,
          }
        ]}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        loading={loading}
        loadingText="Loading resources"
        empty={
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No dependency found</b>
            </SpaceBetween>
          </Box>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Type any keyword to filter dependencies"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            countText={`${filteredPackages.length} matches`}
          />
        }
        header={<Header>Dependencies for package: {packageId}</Header>}
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        }
      />
    </SentinelBasePage>
  );
};
